<section class="page-shell">
  <header class="page-hero dbw-card dbw-card--xl">
    <div>
      <p class="eyebrow">Atlas</p>
      <h3>ArianeId {{ inspection()?.group?.arianeId ?? arianeId() }}</h3>
    </div>

    <div class="hero-actions">
      <oui-button
        color="secondary"
        type="outlined"
        iconSuffix="sync"
        (click)="reload()"
        [disabled]="loading()">
        Recharger
      </oui-button>
    </div>
  </header>

  @if (loading()) {
    <dbw-state-card appearance="loading">
      <p>Chargement de l'inspection du doublon Ariane en cours.</p>
    </dbw-state-card>
  }

  @if (error() && !loading()) {
    <dbw-state-card
      appearance="error"
      title="Chargement impossible">
      <p>{{ error() }}</p>
    </dbw-state-card>
  }

  @if (inspection(); as vm) {
    <section class="panel dbw-card dbw-card--xl">
      <div class="panel-heading">
        <div>
          <h4>Comparatif des investisseurs</h4>
          <p>Les champs divergents sont signalés en orange dans la première colonne.</p>
        </div>
      </div>

      <div class="difference-table-shell">
        <oui-table
          class="data-table"
          [lineSizing]="'dense'">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>Champ</th>
                @for (investor of vm.investors; track investor.investor.investorId; let index = $index) {
                  <th>
                    <dbw-investor-card
                      [id]="investor.investor.investorId"
                      [code]="investor.investor.investorCode || 'Code inconnu'"
                      [name]="investor.investor.displayName || 'Investisseur sans nom'"
                      [statusCode]="investor.investor.statusCode"></dbw-investor-card>
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of investorComparisonRows(); track trackComparisonRow($index, row)) {
                <tr>
                  <th [class.comparison-field--different]="row.hasDifferences">
                    {{ row.label }}
                  </th>
                  @for (investor of vm.investors; track investor.investor.investorId) {
                    <td>
                      @if (row.key === 'actions') {
                        <div class="comparison-actions">
                          @if (canExecutePurgeAction()) {
                            <oui-button
                              type="text"
                              iconSuffix="delete"
                              (click)="openPurgeDialog(investor)">
                              Purger
                            </oui-button>
                          }
                        </div>
                      } @else {
                        @switch (row.key) {
                          @case ('statusCode') {
                            <dbw-investor-status-tag [statusCode]="investor.investor.statusCode"></dbw-investor-status-tag>
                          }
                          @case ('consultant') {
                            <dbw-consultant-card
                              [id]="investor.investor.consultantId"
                              [code]="investor.investor.consultantCode || '-'"
                              [name]="investor.investor.consultantName || '-'"
                              [statusCode]="investor.investor.consultantStatusCode"></dbw-consultant-card>
                          }
                          @case ('boxPlacementOperations') {
                            <oui-tag
                              type="status"
                              size="big"
                              [status]="getBooleanStatus(investor.hasBoxPlacementOperations)">
                              {{ getBooleanLabel(investor.hasBoxPlacementOperations) }}
                            </oui-tag>
                          }
                          @case ('immoOptionIntention') {
                            <oui-tag
                              type="status"
                              size="big"
                              [status]="getBooleanStatus(investor.hasImmoOptionIntention)">
                              {{ getBooleanLabel(investor.hasImmoOptionIntention) }}
                            </oui-tag>
                          }
                          @case ('relationCount') {
                            <div class="comparison-tag-list">
                              <span>{{ investor.relationCount }} relation(s)</span>
                              <oui-tag
                                type="status"
                                size="big"
                                [status]="getCrossConsultantRelationsStatus(investor)">
                                {{ getCrossConsultantRelationsLabel(investor) }}
                              </oui-tag>
                            </div>
                          }
                          @case ('corrections') {
                            <div class="comparison-tag-list">
                              <oui-tag
                                type="status"
                                size="big"
                                [status]="getCorrectionMetadataCount(investor) > 0 ? 'to-correct' : 'completed'">
                                {{ getCorrectionMetadataCount(investor) }} correction(s)
                              </oui-tag>
                              @if (getCorrectionMetadataIds(investor).length > 0) {
                                <span>{{ getCorrectionMetadataIds(investor).join(', ') }}</span>
                              }
                            </div>
                          }
                          @case ('anomalies') {
                            <div class="comparison-tag-list">
                              <oui-tag
                                type="status"
                                size="big"
                                [status]="getAnomalyMetadataCount(investor) > 0 ? 'error' : 'completed'">
                                {{ getAnomalyMetadataCount(investor) }} anomalie(s)
                              </oui-tag>
                              @if (getAnomalyMetadataIds(investor).length > 0) {
                                <span>{{ getAnomalyMetadataIds(investor).join(', ') }}</span>
                              }
                            </div>
                          }
                          @case ('metadatas') {
                            @if (investor.metadatas.length === 0) {
                              <span>Aucune</span>
                            } @else {
                              <div class="comparison-stack">
                                @for (metadata of investor.metadatas; track metadata.metadataId) {
                                  <div class="comparison-tag-list">
                                    <span>Metadata {{ metadata.metadataId }}</span>
                                    <oui-tag
                                      type="status"
                                      size="big"
                                      [status]="getMetadataBadgeStatus(metadata)">
                                      {{ getMetadataBadgeLabel(metadata) }}
                                    </oui-tag>
                                    @if (metadata.investorIsAutomaticSuggestion) {
                                      <oui-tag
                                        type="status"
                                        size="big"
                                        status="completed">
                                        Suggestion auto
                                      </oui-tag>
                                    }
                                    <oui-button
                                      type="text"
                                      color="secondary"
                                      (click)="openMetadata(metadata.metadataId)">
                                      Ouvrir
                                    </oui-button>
                                  </div>
                                }
                              </div>
                            }
                          }
                          @case ('deletionScore') {
                            @if (getDeletionCandidate(investor.investor.investorId); as candidate) {
                              <div class="score-cell">
                                <strong>{{ candidate.totalScore }}</strong>
                                <dbw-investor-deletion-score-details
                                  [investorId]="candidate.investorId"
                                  [evaluations]="candidate.evaluations"></dbw-investor-deletion-score-details>
                                <oui-tag
                                  type="status"
                                  size="big"
                                  [status]="getDeletionDecisionStatus(candidate)">
                                  {{ getDeletionDecisionLabel(candidate) }}
                                </oui-tag>
                              </div>
                            } @else {
                              <oui-tag
                                type="status"
                                size="big"
                                status="pending">
                                Non calculé
                              </oui-tag>
                            }
                          }
                          @default {
                            {{ getComparisonDisplayValue(row.key, investor) }}
                          }
                        }
                      }
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </oui-table>
      </div>
    </section>
  }
</section>
